import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
// Plugins
import { NzSelectOptionInterface } from 'ng-zorro-antd/select';
import { Observable, Subject, shareReplay } from 'rxjs';
import { SessionStorage } from 'ngx-webstorage';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';
// Models
import { Response } from '@models/api/api.model';
import { AuthData } from '@models/auth/auth.model.ro';
import { CreatePackageDTO, CreatePaywallPackageDTO, CreateStripeProductDTO, UpdatePaywallPackageDTO } from '@models/payment/payment.model.dto';
import { CreatePaywallPackageRO, CurrenciesListRO, PaywallBundlesRO, PaywallPackagesRO, StripeProductsRO, UpdatePaywallPackagesRO } from '@models/payment/payment.model.ro';
import { CreditCardPaymentFormControls, PaywallPackagePermissions, StripePackageFormControls, StripePackageServiceFormControls, StripeProductPrice } from '@models/payment/payment.model';
// Shared
import { APP_HOST, ENDPOINTS } from '@shared/endpoints.enum';
import { adminUsers } from '@shared/config';
// Services
import { HttpService } from '@services/http/http.service';

@Injectable({
	providedIn: 'root'
})
export class PaymentService {

	@SessionStorage() public authData: AuthData;

	private currencies$: Observable<CurrenciesListRO> = null;
	public paywallBundleChange$ = new Subject<NzSelectOptionInterface>();

	public constructor(
		private httpServcice: HttpService,
		private router: Router
	) { }

	public getCurrenciesList(): Observable<CurrenciesListRO> {
		if (!this.currencies$) {
			this.currencies$ = this.httpServcice.request<null, CurrenciesListRO>(
				'get', ENDPOINTS.CURRENCIES_LIST, APP_HOST.PHP, null, false, true
			).pipe(shareReplay(1));
		}

		return this.currencies$;
	}

	public createPackage(formControls: CreditCardPaymentFormControls): Observable<Response> {
		let data: Partial<CreatePackageDTO> = {
			user_uuid: this.authData?.user_data?.uuid,
			sales_person_uuid: this.authData?.user_data?.uuid
		};

		for (let key in formControls) data[key] = formControls[key].value;

		return this.httpServcice.request('post', ENDPOINTS.CREATE_PACKAGE, APP_HOST.PHP, data, false, false, false);
	}

	public getStripeProducts(): Observable<StripeProductsRO> {
		return this.httpServcice.request('post', ENDPOINTS.READ_STRIPE_PRODUCTS, APP_HOST.PHP, null, false, true, false);
	}

	public createStripeProduct(
		formControls: StripePackageFormControls, 
		mandatoryServices: Array<FormGroup<StripePackageServiceFormControls>>,
		optionalServices: Array<FormGroup<StripePackageServiceFormControls>>,
		advancedSettings: boolean
	): Observable<Response> {
		let paymentTypes: Array<string> = [];
		let data: CreateStripeProductDTO = {
			user_uuid: this.authData?.user_data?.uuid,
			package_name: formControls.packageName.value,
			card_eft: '',
			days_past_permitted: formControls.pastDays.value,
			days_future_permitted: formControls.futureDays.value,
			price_items: []
		};

		if (formControls.paymentTypeCard.value) paymentTypes.push('card');

		if (formControls.paymentTypeCardSepaDebit.value) paymentTypes.push('sepa_debit');

		data.card_eft = paymentTypes.join(',');

		if (advancedSettings) {
			if (formControls.defaultDay.value) data.subscription_start_date_ts = moment(formControls.defaultDay.value).valueOf();

			if (formControls.startDate.value) data.fStartTs = moment(formControls.startDate.value).valueOf();

			if (formControls.endDate.value) data.fEndTs = moment(formControls.endDate.value).valueOf();
		}

		if (mandatoryServices && mandatoryServices?.length) {
			data.price_items = this.getStripeProductPriceDTO(mandatoryServices, formControls.currency.value, 1);
		}

		if (optionalServices && optionalServices?.length) {
			data.price_items = [...data.price_items, ...this.getStripeProductPriceDTO(optionalServices, formControls.currency.value, 0)];
		}

		return this.httpServcice.request('post', ENDPOINTS.CREATE_STRIPE_PRODUCT, APP_HOST.PHP, data, false, false, false);
	}

	private getStripeProductPriceDTO(
		data: Array<FormGroup<StripePackageServiceFormControls>>, 
		currency: string,
		mandatory: number
	): Array<Partial<StripeProductPrice>> {
		let priceItems: Array<Partial<StripeProductPrice>> = [];

		data.forEach((item: FormGroup<StripePackageServiceFormControls>) => {
			let priceItem: Partial<StripeProductPrice> = {
				name: item.controls.name.value,
				mandatory,
				recurring: item.controls.interval.value,
				currency: currency,
				amount: item.controls.price.value * 100,
				qty_editable: item.controls.quantityEditable.value ? 1 : 0
			};
	
			if (item.controls.quantityEditable.value) {
				priceItem.qty_default = item.controls.defaultQuantity.value;
				priceItem.qty_min = item.controls.minQuantity.value;
				priceItem.qty_max = item.controls.maxQuantity.value;
			}

			priceItems.push(priceItem);
		});

		return priceItems;
	}

	public getPaywallBundles(): Observable<PaywallBundlesRO> {
        return this.httpServcice.request('post', ENDPOINTS.SNAPFIX_BUNDLES, APP_HOST.PHP, null, false, true, false);
    }

	public getPaywallPackages(id: string): Observable<PaywallPackagesRO> {
		return this.httpServcice.request('post', ENDPOINTS.SNAPFIX_PACKAGES, APP_HOST.PHP, { bundle_ids: [id] }, false, false, false);
    }

	public createPaywallPackage(data: CreatePaywallPackageDTO): Observable<CreatePaywallPackageRO> {
		return this.httpServcice.request('post', ENDPOINTS.CREATE_SNAPFIX_PACKAGE, APP_HOST.PHP, data, false, false, true);
    }

	public updatePaywallPackage(data: UpdatePaywallPackageDTO): Observable<UpdatePaywallPackagesRO> {
		return this.httpServcice.request('post', ENDPOINTS.UPDATE_PACKAGE_SCHEMA, APP_HOST.PHP, data, false, false, true);
    }

	public validatePaywallPackagePermissionsLimit(data: PaywallPackagePermissions): string {
        let errorValue: string = null;
        let limitsKeys: Array<string> = ['task', 'group', 'user'];
        let appTrialDaysKeys: Array<string> = ['ios', 'android', 'web'];

        for (let key of limitsKeys) {
            if (data?.permission?.[key]?.limit < data?.permission?.[key]?.limit_warning) {
                errorValue = `${ key } limit should be greater than limit_warning`; 

                break;
            }

            if (data?.permission?.[key]?.limit_year < data?.permission?.[key]?.limit_year_warning) {
                errorValue = `${ key } limit_year should be greater than limit_year_warning`; 

                break;
            }
        }

        if (!errorValue) {
            for (let appKey of appTrialDaysKeys) {
                if (data?.permission?.apps?.[appKey]?.free_trial_days < data?.permission?.apps?.[appKey]?.free_trial_days_warning) {
                    errorValue = `${ appKey } free_trial_days should be greater than free_trial_days_warning`; 

                    break;
                }
            }
        }

        return errorValue;
    }

	public checkRestrictionAccess(fromRoute: boolean = false): boolean {
		if (!this.authData?.access_token) return false;

		let identity: string = jwtDecode(this.authData?.access_token)?.['identity'];

		if (
			identity == this.authData?.user_data?.uuid &&
			adminUsers?.includes(this.authData?.user_data?.fUsername)
		) return true;

		if (fromRoute) this.router.navigateByUrl('/');

		return false;
	}
}